import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.get(`/payments/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-background flex items-center justify-center">
      <div className="bg-white rounded-sm p-12 shadow-lg border border-slate-200 text-center max-w-md">
        {status === 'checking' && (
          <>
            <Loader className="w-16 h-16 text-secondary animate-spin mx-auto mb-6" />
            <h2 className="font-manrope font-bold text-2xl text-primary mb-3" data-testid="payment-processing">
              Processing Payment
            </h2>
            <p className="text-slate-600 font-public-sans">Please wait while we confirm your payment...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h2 className="font-manrope font-bold text-2xl text-primary mb-3" data-testid="payment-success">
              Payment Successful!
            </h2>
            <p className="text-slate-600 font-public-sans mb-6">
              Your booking has been confirmed. You will receive a confirmation email shortly.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-primary text-white hover:bg-primary-900 rounded-sm px-8 py-3 font-semibold uppercase"
              data-testid="view-bookings-button"
            >
              View My Bookings
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <h2 className="font-manrope font-bold text-2xl text-red-600 mb-3">Payment Error</h2>
            <p className="text-slate-600 font-public-sans mb-6">There was an error processing your payment.</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-primary text-white hover:bg-primary-900 rounded-sm px-8 py-3"
            >
              Go to Home
            </Button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <h2 className="font-manrope font-bold text-2xl text-primary mb-3">Payment Status Check Timed Out</h2>
            <p className="text-slate-600 font-public-sans mb-6">
              Please check your email for confirmation or view your bookings.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="bg-primary text-white hover:bg-primary-900 rounded-sm px-8 py-3">
              View My Bookings
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;